import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Typography, Tag, Empty, Spin, Button,
  Space, Badge, Steps, Tooltip, Divider,
} from 'antd';
import {
  ShoppingOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CrownOutlined, StarOutlined, GiftOutlined, RightOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import { orderAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

interface Order {
  id: number;
  order_no: string;
  plan_type: 'free' | 'standard' | 'premium';
  amount: number;
  payment_status: 'PENDING' | 'PAID' | 'EXPIRED' | 'REFUNDED';
  payment_method?: string;
  tool_name: string;
  tool_url?: string;
  tool_id?: number;
  benefit_status: 'INACTIVE' | 'ACTIVE' | 'EXPIRED' | 'REVOKED';
  benefit_activated_at?: string;
  benefit_expires_at?: string;
  benefit_note?: string;
  paid_at?: string;
  created_at: string;
  tool?: { id: number; name: string; slug: string; logo?: string };
}

const MyOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);
  const { t, language } = useLanguage();
  const isZh = language === 'zh-CN';

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await orderAPI.getMyOrders({ page: 1, pageSize: 50 });
      setOrders(res.data.orders || []);
    } catch (error) {
      console.error('获取订单失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchOrders();
  }, [user, authLoading, navigate, fetchOrders]);

  const planConfig: Record<string, { color: string; icon: React.ReactNode; gradient: string }> = {
    free: { color: '#52c41a', icon: <CheckCircleOutlined />, gradient: 'linear-gradient(135deg, #f6ffed, #e6fffb)' },
    standard: { color: '#1677ff', icon: <StarOutlined />, gradient: 'linear-gradient(135deg, #e6f4ff, #f0e6ff)' },
    premium: { color: '#faad14', icon: <CrownOutlined />, gradient: 'linear-gradient(135deg, #fff7e6, #fff1f0)' },
  };

  const activeOrders = orders.filter(o => o.benefit_status === 'ACTIVE');
  const pendingOrders = orders.filter(o => o.payment_status === 'PENDING');

  // ==================== 权益卡片 ====================
  const renderBenefitCard = (order: Order) => {
    const cfg = planConfig[order.plan_type] || planConfig.free;
    const daysLeft = order.benefit_expires_at
      ? Math.max(0, dayjs(order.benefit_expires_at).diff(dayjs(), 'day'))
      : null;
    const isExpiring = daysLeft !== null && daysLeft < 7;

    return (
      <Col xs={24} md={12} key={order.id}>
        <Card
          style={{
            borderRadius: 16,
            background: cfg.gradient,
            border: `2px solid ${cfg.color}30`,
            overflow: 'hidden',
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Row justify="space-between" align="top">
            <Col>
              <Space>
                <span style={{ fontSize: 28, color: cfg.color }}>{cfg.icon}</span>
                <div>
                  <Text strong style={{ fontSize: 18 }}>{order.tool_name}</Text>
                  <br />
                  <Tag color={cfg.color} style={{ marginTop: 4 }}>
                    {order.plan_type === 'free' ? t('orders.planFree') :
                      order.plan_type === 'standard' ? t('orders.planStd') :
                        t('orders.planPro')}
                  </Tag>
                </div>
              </Space>
            </Col>
            <Col>
              <Badge status="processing" color={isExpiring ? 'orange' : 'green'} />
              <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>
                {t('orders.benefitActive')}
              </Text>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }} />

          {/* 权益列表 */}
          <div style={{ marginBottom: 16 }}>
            {order.plan_type === 'standard' && (
              <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 2 }}>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitStd1')}</li>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitStd2')}</li>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitStd3')}</li>
              </ul>
            )}
            {order.plan_type === 'premium' && (
              <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 2 }}>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro1')}</li>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro2')}</li>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro3')}</li>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro4')}</li>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro5')}</li>
              </ul>
            )}
            {order.plan_type === 'free' && (
              <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 2 }}>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitFree1')}</li>
                <li><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitFree2')}</li>
              </ul>
            )}
          </div>

          <Row justify="space-between" align="bottom">
            <Col>
              {daysLeft !== null && (
                <Tooltip title={`${t('orders.expiresAt')}: ${dayjs(order.benefit_expires_at).format('YYYY-MM-DD')}`}>
                  <Tag
                    color={isExpiring ? 'orange' : 'default'}
                    icon={isExpiring ? <ExclamationCircleOutlined /> : <ClockCircleOutlined />}
                  >
                    {isExpiring
                      ? `${t('orders.expiringIn')} ${daysLeft} ${t('orders.days')}`
                      : `${t('orders.remaining')} ${daysLeft} ${t('orders.days')}`}
                  </Tag>
                </Tooltip>
              )}
            </Col>
            <Col>
              {order.tool && (
                <Button
                  type="link"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => navigate(`/tools/${order.tool!.slug}`)}
                >
                  {t('orders.viewTool')}
                </Button>
              )}
            </Col>
          </Row>
        </Card>
      </Col>
    );
  };

  // ==================== 订单行 ====================
  const renderOrderRow = (order: Order) => {
    const cfg = planConfig[order.plan_type] || planConfig.free;

    const statusStep =
      order.payment_status === 'PAID' && order.benefit_status === 'ACTIVE' ? 2 :
        order.payment_status === 'PAID' ? 1 : 0;

    return (
      <Card
        key={order.id}
        style={{ borderRadius: 12, marginBottom: 12 }}
        bodyStyle={{ padding: 16 }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 12]}>
          <Col xs={24} sm={8}>
            <Space>
              <span style={{ fontSize: 20, color: cfg.color }}>{cfg.icon}</span>
              <div>
                <Text strong>{order.tool_name}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                  {order.order_no}
                </Text>
              </div>
            </Space>
          </Col>

          <Col xs={12} sm={4}>
            <Tag color={cfg.color}>
              {order.plan_type === 'free' ? t('orders.planFree') :
                order.plan_type === 'standard' ? t('orders.planStd') :
                  t('orders.planPro')}
            </Tag>
            <br />
            <Text strong style={{ color: order.amount > 0 ? '#f5222d' : '#52c41a' }}>
              ¥{Number(order.amount).toFixed(2)}
            </Text>
          </Col>

          <Col xs={12} sm={8}>
            <Steps
              size="small"
              current={statusStep}
              status={order.payment_status === 'EXPIRED' || order.payment_status === 'REFUNDED' ? 'error' : 'process'}
              items={[
                { title: t('orders.stepSubmitted') },
                { title: t('orders.stepPaid') },
                { title: t('orders.stepActivated') },
              ]}
            />
          </Col>

          <Col xs={24} sm={4} style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(order.created_at).format('YYYY-MM-DD')}
            </Text>
            <br />
            {order.payment_status === 'PENDING' && (
              <Tag color="orange" icon={<ClockCircleOutlined />}>{t('orders.statusPending')}</Tag>
            )}
            {order.payment_status === 'REFUNDED' && (
              <Tag color="red">{t('orders.statusRefunded')}</Tag>
            )}
            {order.benefit_status === 'ACTIVE' && (
              <Tag color="green" icon={<GiftOutlined />}>{t('orders.benefitActive')}</Tag>
            )}
          </Col>
        </Row>
      </Card>
    );
  };

  if (authLoading || loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <div className="page-container" style={{ maxWidth: 960, margin: '0 auto', padding: '32px 16px' }}>
      <Title level={2} style={{ marginBottom: 8 }}>
        <ShoppingOutlined style={{ marginRight: 8 }} />
        {t('orders.myOrdersTitle')}
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 32 }}>
        {t('orders.myOrdersSubtitle')}
      </Paragraph>

      {/* ==================== 活跃权益 ==================== */}
      {activeOrders.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <Title level={4}>
            <GiftOutlined style={{ color: '#eb2f96', marginRight: 8 }} />
            {t('orders.activeBenefitsTitle')}
          </Title>
          <Row gutter={[16, 16]}>
            {activeOrders.map(renderBenefitCard)}
          </Row>
        </div>
      )}

      {/* ==================== 待支付提醒 ==================== */}
      {pendingOrders.length > 0 && (
        <Card
          style={{ marginBottom: 24, borderRadius: 12, borderColor: '#faad14' }}
          bodyStyle={{ padding: 16 }}
        >
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
            <Text strong style={{ color: '#faad14' }}>
              {t('orders.pendingHint', { count: pendingOrders.length })}
            </Text>
            <Button type="link" onClick={() => navigate('/submit')}>
              {t('orders.goToPay')}
            </Button>
          </Space>
        </Card>
      )}

      {/* ==================== 全部订单 ==================== */}
      <Title level={4} style={{ marginBottom: 16 }}>
        {t('orders.allOrders')} ({orders.length})
      </Title>

      {orders.length > 0 ? (
        orders.map(renderOrderRow)
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('orders.noOrders')}
          style={{ padding: 60 }}
        >
          <Button type="primary" onClick={() => navigate('/submit')}>
            {t('orders.submitTool')}
          </Button>
        </Empty>
      )}

      {/* ==================== 权益对比 ==================== */}
      <Divider />
      <Card
        style={{ borderRadius: 16, background: 'linear-gradient(135deg, #f5f5f5, #fafafa)' }}
        bodyStyle={{ padding: 24 }}
      >
        <Title level={4} style={{ textAlign: 'center', marginBottom: 24 }}>{t('orders.benefitCompare')}</Title>
        <Row gutter={[16, 16]}>
          {/* Free */}
          <Col xs={24} md={8}>
            <Card style={{ borderRadius: 12, border: '2px solid #b7eb8f', textAlign: 'center', height: '100%' }}>
              <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a' }} />
              <Title level={5} style={{ color: '#52c41a', margin: '12px 0 8px' }}>{t('orders.planFree')}</Title>
              <Text style={{ fontSize: 24, fontWeight: 700 }}>{isZh ? '¥0' : '$0'}</Text>
              <Divider />
              <div style={{ textAlign: 'left' }}>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitFree1')}</Paragraph>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitFree2')}</Paragraph>
              </div>
            </Card>
          </Col>
          {/* Standard */}
          <Col xs={24} md={8}>
            <Card style={{ borderRadius: 12, border: '2px solid #1677ff', textAlign: 'center', height: '100%', boxShadow: '0 4px 12px rgba(22,119,255,0.15)' }}>
              <StarOutlined style={{ fontSize: 32, color: '#1677ff' }} />
              <Title level={5} style={{ color: '#1677ff', margin: '12px 0 8px' }}>{t('orders.planStd')}</Title>
              <Text style={{ fontSize: 24, fontWeight: 700 }}>{isZh ? '¥50' : '$7'}</Text>
              <Divider />
              <div style={{ textAlign: 'left' }}>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitStd1')}</Paragraph>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitStd2')}</Paragraph>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitStd3')}</Paragraph>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitStd4')}</Paragraph>
              </div>
            </Card>
          </Col>
          {/* Premium */}
          <Col xs={24} md={8}>
            <Card style={{ borderRadius: 12, border: '2px solid #faad14', textAlign: 'center', height: '100%' }}>
              <CrownOutlined style={{ fontSize: 32, color: '#faad14' }} />
              <Title level={5} style={{ color: '#faad14', margin: '12px 0 8px' }}>{t('orders.planPro')}</Title>
              <Text style={{ fontSize: 24, fontWeight: 700 }}>{isZh ? '¥698' : '$99'}</Text>
              <Divider />
              <div style={{ textAlign: 'left' }}>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro1')}</Paragraph>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro2')}</Paragraph>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro3')}</Paragraph>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro4')}</Paragraph>
                <Paragraph><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />{t('orders.benefitPro5')}</Paragraph>
              </div>
            </Card>
          </Col>
        </Row>
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Button type="primary" size="large" onClick={() => navigate('/submit')}>
            {t('orders.submitTool')}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default MyOrdersPage;
